import { ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { from, Observable, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

import { EventTypes, ItemStates, FolderKinds, List } from '@site-mate/dashpivot-shared-library';

import { TmpI18NService } from 'app/i18n/tmp-i18n.service';
import { ListsService } from 'app/lists/lists.service';
import { SegmentService } from 'app/segment/segment.service';
import { ListsHandler } from 'app/shared/model/lists/lists-handler.model';
import { ConfirmService } from 'app/shared/service/confirm.service';
import { ErrorHandler } from 'app/shared/service/error-handler.service';
import { EventsService } from 'app/shared/service/events/events.service';
import { ToastrService } from 'app/shared/service/toastr.service';

@Component({ template: '<div></div>' })
export abstract class AbstractListPage implements OnDestroy {
  public readonly parentType: string = 'none';
  public readonly fullDisplayMode = 6;
  public readonly canDeployLists: boolean = false;
  protected readonly destroy$ = new Subject<void>();
  protected parentId: string;
  lists: List[] = [];
  readonly state = {
    isLoading: false,
    selectedListIds: [] as string[],
  };
  folderKind: FolderKinds;

  constructor(
    protected readonly activatedRoute: ActivatedRoute,
    protected readonly changeDetectorRef: ChangeDetectorRef,
    protected readonly confirmService: ConfirmService,
    protected readonly errorHandler: ErrorHandler,
    protected readonly listsService: ListsService,
    protected readonly router: Router,
    protected readonly segmentService: SegmentService,
    protected readonly toastrService: ToastrService,
    protected readonly eventsService: EventsService,
    protected readonly i18nService: TmpI18NService,
  ) {}

  abstract getLists(parentId: string, state: ItemStates): Observable<List[]>;

  onSelectList(listId: string) {
    return this.router.navigate(['/', 'lists', this.parentType, this.parentId, listId]);
  }

  onChangeSelectedState(listId: string, selected: boolean) {
    const updatedListIds = this.state.selectedListIds.filter((item) => item !== listId);
    if (selected) {
      updatedListIds.push(listId);
    }
    this.state.selectedListIds = updatedListIds;
  }

  onListsArchived(listIds: string[]) {
    const confirmButtonText = this.i18nService.getMessage('confirmArchive');
    const confirmLabel =
      this.folderKind === FolderKinds.Company ? 'archiveCompanyList' : 'archiveProjectList';

    from(this.confirmService.confirmDelete(confirmLabel, { confirmButtonText })).subscribe({
      next: () => {
        this.onListsAction({
          actionFn: this.listsService.archiveLists.bind(this.listsService),
          listIds,
          successLabel: 'listArchived',
          errorLabel: 'listArchivedError',
          context: 'Archived',
        });
      },
      error: () => {},
    });
  }

  onListsRestored(listIds: string[]) {
    this.onListsAction({
      actionFn: this.listsService.restoreLists.bind(this.listsService),
      listIds,
      successLabel: 'listRestored',
      errorLabel: 'listRestoredError',
      context: 'Restored',
    });
  }

  onListsDelete(listIds: string[]) {
    const confirmButtonText = this.i18nService.getMessage('confirmDelete');
    const confirmLabel = this.folderKind === FolderKinds.Company ? 'deleteCompanyList' : 'deleteProjectList';

    from(this.confirmService.confirmDelete(confirmLabel, { confirmButtonText })).subscribe({
      next: () => {
        this.onListsAction({
          actionFn: this.listsService.deleteLists.bind(this.listsService),
          listIds,
          successLabel: 'listRemoved',
          errorLabel: 'listRemovedError',
          context: 'Deleted',
        });
      },
      error: () => {},
    });
  }

  onListsAction({
    actionFn,
    listIds,
    successLabel,
    errorLabel,
    context,
  }: {
    actionFn: (parentId: string, listIds: string[], folderKind: FolderKinds) => Observable<List[]>;
    listIds: string[];
    successLabel: string;
    errorLabel?: string;
    context: string;
  }) {
    const listsBeforeArchive = this.lists;
    const selectedListsIdsBeforeArchive = this.state.selectedListIds;
    this.lists = this.lists.filter((list) => !listIds.includes(list._id));
    this.state.selectedListIds = [];

    actionFn(this.parentId, listIds, this.folderKind)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toastrService.successByKey(successLabel);
          this.eventsService.trackEvent(EventTypes.ListActionApplied, { Context: context });
        },
        error: (error) => {
          this.lists = listsBeforeArchive;
          this.state.selectedListIds = selectedListsIdsBeforeArchive;
          const errorToDisplay = errorLabel ? { toastKey: errorLabel } : error;
          this.errorHandler.handle(errorToDisplay);
        },
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected loadLists(state: ItemStates) {
    this.state.isLoading = true;
    this.getLists(this.parentId, state)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.state.isLoading = false;
        }),
      )
      .subscribe(
        (lists) => {
          const filteredLists = lists.map((list) =>
            ListsHandler.service.filterDeployedListItems(list, this.parentId),
          );
          this.lists = filteredLists;
        },
        (error) => {
          this.errorHandler.handle(error);
        },
      );
  }
}
